import BaseTrackingService from '../BaseTrackingService';
import { EVENTS } from './trackEvents';

class LoggedCheckoutTrackingService extends BaseTrackingService {
  static EVENT_LOGGED_CHECKOUT_CARD_TOKENIZATION_SUCCESS =
    EVENTS.LOGGED_CHECKOUT_CARD_TOKENIZATION_SUCCESS;
  static EVENT_LOGGED_CHECKOUT_CARD_TOKENIZATION_ERROR =
    EVENTS.LOGGED_CHECKOUT_CARD_TOKENIZATION_ERROR;
  static EVENT_LOGGED_CHECKOUT_PAYMENT_FAILED =
    EVENTS.LOGGED_CHECKOUT_PAYMENT_FAILED;
  static EVENT_LOGGED_CHECKOUT_PAYMENT_SUCCESS =
    EVENTS.LOGGED_CHECKOUT_PAYMENT_SUCCESS;

  static EVENT_LOGGED_CHECKOUT_PIX_PAYMENT_PAID =
    EVENTS.LOGGED_CHECKOUT_PIX_PAYMENT_PAID;
  static EVENT_LOGGED_CHECKOUT_TALK_TO_SUPPORT_CLICKED =
    EVENTS.LOGGED_CHECKOUT_TALK_TO_SUPPORT_CLICKED;
  static EVENT_LOGGED_CHECKOUT_PAYMENT_METHOD_CHANGED =
    EVENTS.LOGGED_CHECKOUT_PAYMENT_METHOD_CHANGED;
  static EVENT_LOGGED_CHECKOUT_PAYMENT_BOLETO_CLICKED =
    EVENTS.LOGGED_CHECKOUT_PAYMENT_BOLETO_CLICKED;
  static EVENT_LOGGED_CHECKOUT_PAY_INVOICE_BUTTON_CLICKED =
    EVENTS.LOGGED_CHECKOUT_PAY_INVOICE_BUTTON_CLICKED;

  constructor() {
    super('CheckoutTrackingService');
  }

  /**
   * Monta propriedades para `trackLoggedCheckoutCardTokenizationSuccess` em trackEvents.ts.
   * @param {Object|null} [pixPaymentData] - Contexto Pix quando o fluxo mistura Pix + cartão.
   */
  prepareLoggedCheckoutCardTokenizationSuccessForAnalytics(
    formikValues,
    tokenData,
    coupon = {},
    defaultTrialDays = 7,
    pixPaymentData = null,
  ) {
    const userData = this._buildUserData(formikValues);
    const cardData = this._buildCardData(formikValues);
    const checkoutData = this._buildCheckoutData(
      formikValues,
      coupon,
      defaultTrialDays,
    );

    return this._buildLoggedCheckoutCardTokenizationSuccessTrackingData(
      cardData,
      userData,
      tokenData,
      checkoutData,
      pixPaymentData,
    );
  }

  /**
   * Monta o payload de `logged_checkout_card_tokenization_success`.
   * @param {Object} cardData - Dados do cartão
   * @param {Object} userData - Dados do usuário
   * @param {Object} tokenData - Dados do token gerado
   * @param {Object} checkoutData - Dados do checkout (plano, cupom, etc.)
   * @param {Object|null} pixPaymentData - Contexto Pix opcional (ids/status/amounts; sem QR/código)
   */
  _buildLoggedCheckoutCardTokenizationSuccessTrackingData(
    cardData,
    userData,
    tokenData,
    checkoutData = {},
    pixPaymentData = null,
  ) {
    const trackingData = {
      // Informações do cliente
      ...this._buildSegmentUserEventProperties(userData),

      // Informações do cartão
      card_hash: tokenData.id || tokenData.card_hash,
      card_number_masked: this._maskCardNumber(cardData.card_number),
      card_brand: this._getCardBrand(cardData.card_number),

      // Informações do checkout
      plan_id: checkoutData.plan_id,
      plan_name: checkoutData.plan_name,
      plan_amount: checkoutData.plan_amount,
      plan_type: checkoutData.plan_type,
      coupon_code: checkoutData.coupon_code,
      coupon_valid: checkoutData.coupon_valid,
      trial_days: checkoutData.trial_days,
      has_compra_garantida: checkoutData.has_compra_garantida,

      // Metadados da operação
      context: 'checkout',
      timestamp: new Date().toISOString(),
      provider: 'pagarme',
      status: 'success',

      // Informações adicionais para Metabase
      event_type:
        LoggedCheckoutTrackingService.EVENT_LOGGED_CHECKOUT_CARD_TOKENIZATION_SUCCESS,
      conversion_value: 1,
      is_first_card: true, // No checkout sempre é o primeiro cartão
      checkout_step: 'step2',
    };

    if (!pixPaymentData || typeof pixPaymentData !== 'object') {
      return trackingData;
    }

    const gatewayData = pixPaymentData.gatewayData;

    return {
      ...trackingData,
      pix_payment_amount: pixPaymentData.amount ?? null,
      pix_gateway_amount: gatewayData?.amount ?? null,
      pix_gateway_status: gatewayData?.status ?? null,
      pix_subscription_id: pixPaymentData.subscription_id ?? null,
      pix_subscription_provider_id:
        pixPaymentData.subscriptionProviderId ?? null,
      pix_gateway_last_transaction_status:
        gatewayData?.last_transaction?.status ?? null,
      pix_payment_status: pixPaymentData.status ?? null,
    };
  }

  /**
   * Monta propriedades para `trackLoggedCheckoutCardTokenizationError` em trackEvents.ts.
   * @param {Object|null} [pixPaymentData] - Contexto Pix quando o fluxo mistura Pix + cartão.
   */
  prepareLoggedCheckoutCardTokenizationErrorForAnalytics(
    formikValues,
    error,
    coupon = {},
    defaultTrialDays = 7,
    pixPaymentData = null,
  ) {
    const userData = this._buildUserData(formikValues);
    const cardData = this._buildCardData(formikValues);
    const checkoutData = this._buildCheckoutData(
      formikValues,
      coupon,
      defaultTrialDays,
    );

    return this._buildLoggedCheckoutCardTokenizationErrorTrackingData(
      cardData,
      userData,
      error,
      checkoutData,
      pixPaymentData,
    );
  }

  /**
   * Monta o payload de `logged_checkout_card_tokenization_error`.
   * @param {Object} cardData - Dados do cartão
   * @param {Object} userData - Dados do usuário
   * @param {Object} error - Erro ocorrido
   * @param {Object} checkoutData - Dados do checkout
   * @param {Object|null} pixPaymentData - Contexto Pix opcional
   */
  _buildLoggedCheckoutCardTokenizationErrorTrackingData(
    cardData,
    userData,
    error,
    checkoutData = {},
    pixPaymentData = null,
  ) {
    const trackingData = {
      // Informações do cliente
      ...this._buildSegmentUserEventProperties(userData),

      // Informações do cartão (mascaradas por segurança)
      card_number_masked: this._maskCardNumber(cardData.card_number),
      card_brand: this._getCardBrand(cardData.card_number),

      // Informações do erro
      error_message: error?.error_message || 'Unknown error',
      error_code: error?.error_code || 'UNKNOWN_ERROR',
      error_type: error?.error_type || 'tokenization_error',
      cors_details: error?.cors_details || null,
      // Informações do checkout
      plan_id: checkoutData.plan_id,
      plan_name: checkoutData.plan_name,
      plan_amount: checkoutData.plan_amount,
      plan_type: checkoutData.plan_type,
      coupon_code: checkoutData.coupon_code,
      coupon_valid: checkoutData.coupon_valid,

      // Metadados da operação
      context: 'checkout',
      timestamp: new Date().toISOString(),
      provider: 'pagarme',
      status: 'error',

      // Informações adicionais para Metabase
      event_type:
        LoggedCheckoutTrackingService.EVENT_LOGGED_CHECKOUT_CARD_TOKENIZATION_ERROR,
      conversion_value: 0,
      is_first_card: true,
      checkout_step: 'step2',
    };

    if (!pixPaymentData || typeof pixPaymentData !== 'object') {
      return trackingData;
    }

    const gatewayData = pixPaymentData.gatewayData;

    return {
      ...trackingData,
      pix_payment_amount: pixPaymentData.amount ?? null,
      pix_gateway_amount: gatewayData?.amount ?? null,
      pix_gateway_status: gatewayData?.status ?? null,
      pix_subscription_id: pixPaymentData.subscription_id ?? null,
      pix_subscription_provider_id:
        pixPaymentData.subscriptionProviderId ?? null,
      pix_gateway_last_transaction_status:
        gatewayData?.last_transaction?.status ?? null,
      pix_payment_status: pixPaymentData.status ?? null,
    };
  }

  /**
   * Monta propriedades para `trackLoggedCheckoutPaymentFailed` em trackEvents.ts.
   */
  prepareLoggedCheckoutPaymentFailedForAnalytics(
    formikValues,
    failedReason,
    coupon = {},
    defaultTrialDays = 7,
    subscriptionData = {},
    pixPaymentData = {},
  ) {
    const userData = this._buildUserData(formikValues);
    const cardData = this._buildCardData(formikValues);
    const checkoutData = this._buildCheckoutData(
      formikValues,
      coupon,
      defaultTrialDays,
    );

    return this._buildLoggedCheckoutPaymentFailedTrackingData(
      userData,
      checkoutData,
      failedReason,
      cardData,
      subscriptionData,
      pixPaymentData,
    );
  }

  /**
   * Monta o payload de `logged_checkout_payment_failed`.
   * @param {Object} userData - Dados do usuário
   * @param {Object} checkoutData - Dados do checkout
   * @param {Object} failedReason - Dados do erro de pagamento
   * @param {Object} cardData - Dados do cartão (opcional)
   * @param {Object} subscriptionData - Dados da assinatura (opcional, para extrair transaction_id)
   * @param {Object} pixPaymentData - Dados do pagamento Pix (opcional, para extrair transaction_id)
   */
  _buildLoggedCheckoutPaymentFailedTrackingData(
    userData,
    checkoutData,
    failedReason,
    cardData = {},
    subscriptionData = {},
    pixPaymentData = {},
  ) {
    // Extrai transaction_id do pixPaymentData quando disponível
    const gatewayData = pixPaymentData?.gatewayData || {};
    const lastTransaction = gatewayData?.last_transaction || {};

    const pixTransactionId =
      lastTransaction?.id ||
      pixPaymentData?.transactionId ||
      pixPaymentData?.id ||
      '';

    const pixTransactionStatus =
      lastTransaction?.status ||
      gatewayData?.status ||
      pixPaymentData?.status ||
      '';

    // Extrai transaction_id de múltiplas fontes possíveis (prioriza pixPaymentData)
    const transaction_id =
      pixTransactionId ||
      failedReason?.transaction_id ||
      failedReason?.id ||
      subscriptionData?.id ||
      subscriptionData?.transaction_id ||
      '';

    // Extrai transaction_status de múltiplas fontes possíveis (prioriza pixPaymentData)
    const transaction_status =
      pixTransactionStatus ||
      subscriptionData?.status ||
      failedReason?.status ||
      'failed';

    return {
      // Informações do cliente
      ...this._buildSegmentUserEventProperties(userData),

      // Informações do cartão (se disponível)
      card_number_masked: this._maskCardNumber(cardData.card_number),
      card_brand: this._getCardBrand(cardData.card_number),

      // Informações do erro de pagamento
      error_message:
        failedReason.message ||
        failedReason?.error_message ||
        'Unknown payment error',
      error_details:
        failedReason.details ||
        failedReason?.error_code ||
        'No details available',
      error_code: failedReason?.error_code || 'unknown',
      error_type: failedReason?.error_type || 'payment_failed',

      // Informações da transação
      transaction_id: transaction_id,
      transaction_status: transaction_status,

      // Informações do checkout
      plan_id: checkoutData.plan_id,
      plan_name: checkoutData.plan_name,
      plan_amount: checkoutData.plan_amount,
      plan_type: checkoutData.plan_type,
      coupon_code: checkoutData.coupon_code,
      coupon_valid: checkoutData.coupon_valid,
      trial_days: checkoutData.trial_days,
      has_compra_garantida: checkoutData.has_compra_garantida,

      // Metadados da operação
      context: 'checkout',
      timestamp: new Date().toISOString(),
      provider: 'pagarme',
      status: 'payment_failed',

      // Informações adicionais para Metabase
      event_type:
        LoggedCheckoutTrackingService.EVENT_LOGGED_CHECKOUT_PAYMENT_FAILED,
      conversion_value: 0,
      is_first_card: true,
      checkout_step: 'step2',
    };
  }

  /**
   * Monta propriedades para `trackLoggedCheckoutPaymentSuccess` em trackEvents.ts.
   */
  prepareLoggedCheckoutPaymentSuccessForAnalytics(
    subscriptionData,
    userData,
    checkoutData = {},
    cardData = {},
  ) {
    return this._buildLoggedCheckoutPaymentSuccessTrackingData(
      subscriptionData,
      userData,
      checkoutData,
      cardData,
    );
  }

  /**
   * Monta o payload de `logged_checkout_payment_success`.
   * @param {Object} subscriptionData - Dados da assinatura criada
   * @param {Object} userData - Dados do usuário
   * @param {Object} checkoutData - Dados do checkout
   * @param {Object} cardData - Dados do cartão (opcional)
   */
  _buildLoggedCheckoutPaymentSuccessTrackingData(
    subscriptionData,
    userData,
    checkoutData = {},
    cardData = {},
  ) {
    return {
      // Informações do cliente
      ...this._buildSegmentUserEventProperties(userData),
      customer_document_number: userData.document_number || '',
      customer_name: userData.name || '',
      customer_email: userData.email || '',

      // Informações da transação
      transaction_id:
        subscriptionData?.id || subscriptionData?.transaction_id || '',
      transaction_status: subscriptionData?.status || 'active',

      // Informações do cartão (se disponível)
      card_number_masked: this._maskCardNumber(cardData.card_number),
      card_brand: this._getCardBrand(cardData.card_number),

      // Informações do checkout
      plan_id: checkoutData.plan_id,
      plan_name: checkoutData.plan_name,
      plan_amount: checkoutData.plan_amount,
      plan_type: checkoutData.plan_type,
      coupon_code: checkoutData.coupon_code,
      coupon_valid: checkoutData.coupon_valid,
      trial_days: checkoutData.trial_days,
      has_compra_garantida: checkoutData.has_compra_garantida,

      // Metadados da operação
      timestamp: new Date().toISOString(),
      provider: 'pagarme_v5',
      status: 'success',

      // Informações adicionais para Metabase
      event_type:
        LoggedCheckoutTrackingService.EVENT_LOGGED_CHECKOUT_PAYMENT_SUCCESS,
      conversion_value: 1,
      action: 'payment_success',
    };
  }

  /**
   * Monta propriedades para `trackLoggedCheckoutPixPaymentPaid` em trackEvents.ts.
   */
  prepareLoggedCheckoutPixPaymentPaidForAnalytics(
    transactionData,
    userData,
    checkoutData = {},
    origin = 'checkout',
  ) {
    return this._buildLoggedCheckoutPixPaymentPaidTrackingData(
      transactionData,
      userData,
      checkoutData,
      origin,
    );
  }

  /**
   * Monta o payload de `logged_checkout_pix_payment_paid`.
   * @param {Object} transactionData - Dados da transação Pix
   * @param {Object} userData - Dados do usuário
   * @param {Object} checkoutData - Dados do checkout
   * @param {string} origin - Origem do evento
   */
  _buildLoggedCheckoutPixPaymentPaidTrackingData(
    transactionData,
    userData,
    checkoutData = {},
    origin = 'checkout',
  ) {
    // Prioriza a estrutura do exemplo fornecido
    const gatewayData = transactionData?.gatewayData || {};
    const lastTransaction = gatewayData?.last_transaction || {};
    const customer = gatewayData?.customer || {};

    // Informações da transação - prioriza gatewayData.last_transaction
    const transaction_id =
      lastTransaction?.id ||
      transactionData?.transactionId ||
      transactionData?.id ||
      '';

    const transaction_status =
      lastTransaction?.status ||
      gatewayData?.status ||
      transactionData?.status ||
      'paid';

    const created_at =
      lastTransaction?.created_at ||
      gatewayData?.created_at ||
      transactionData?.createdAt ||
      new Date().toISOString();

    const updated_at =
      lastTransaction?.updated_at ||
      gatewayData?.updated_at ||
      transactionData?.updatedAt ||
      new Date().toISOString();

    // Valor do pagamento - prioriza gatewayData
    const amount =
      gatewayData?.amount ||
      lastTransaction?.amount ||
      gatewayData?.paid_amount ||
      checkoutData?.plan_amount ||
      transactionData?.basePrice ||
      0;

    // Informações do cliente - prioriza gatewayData.customer
    const customer_document_number =
      customer?.document || userData?.document_number || '';

    const customer_name = customer?.name || userData?.name || '';

    const customer_email = customer?.email || userData?.email || '';

    return {
      // Informações do cliente
      ...this._buildSegmentUserEventProperties(userData),
      user_email: userData.email || customer_email,
      user_name: userData.name || customer_name,
      customer_document_number: customer_document_number,
      customer_name: customer_name,
      customer_email: customer_email,

      // Informações da transação
      transaction_id: transaction_id,
      transaction_status: transaction_status,
      created_at: created_at,
      updated_at: updated_at,
      payment_method: 'pix',
      amount: amount,

      // Informações do checkout
      plan_id: checkoutData.plan_id,
      plan_name: checkoutData.plan_name,
      plan_amount: checkoutData.plan_amount,
      plan_type: checkoutData.plan_type,
      coupon_code: checkoutData.coupon_code,
      coupon_valid: checkoutData.coupon_valid,
      trial_days: checkoutData.trial_days,
      has_compra_garantida: checkoutData.has_compra_garantida,

      // Metadados da operação
      context: 'checkout',
      timestamp: new Date().toISOString(),
      provider: 'pagarme_v5',
      status: 'paid',
      event_type:
        LoggedCheckoutTrackingService.EVENT_LOGGED_CHECKOUT_PIX_PAYMENT_PAID,
      conversion_value: 1,
      checkout_step: origin === 'logged-area' ? undefined : 'step2',
      action: 'pix_payment_paid',
      origin: origin,
    };
  }

  /**
   * Monta propriedades para `trackLoggedCheckoutTalkToSupportClicked` em trackEvents.ts.
   */
  prepareLoggedCheckoutTalkToSupportClickedForAnalytics(
    userData,
    checkoutData = {},
    origin = '',
    pixPaymentData = {},
  ) {
    return this._buildLoggedCheckoutTalkToSupportClickedTrackingData(
      userData,
      checkoutData,
      origin,
      pixPaymentData,
    );
  }

  /**
   * Monta o payload de `logged_checkout_talk_to_support_clicked`.
   * @param {Object} userData - Dados do usuário
   * @param {Object} checkoutData - Dados do checkout
   * @param {string} origin - Origem do clique ('pix' ou 'credit-card')
   * @param {Object} pixPaymentData - Dados do pagamento Pix (opcional, para extrair transaction_id)
   */
  _buildLoggedCheckoutTalkToSupportClickedTrackingData(
    userData,
    checkoutData = {},
    origin = '',
    pixPaymentData = {},
  ) {
    // Extrai transaction_id e transaction_status do pixPaymentData quando disponível
    const gatewayData = pixPaymentData?.gatewayData || {};
    const lastTransaction = gatewayData?.last_transaction || {};

    const transaction_id =
      lastTransaction?.id ||
      pixPaymentData?.transactionId ||
      pixPaymentData?.id ||
      '';

    const transaction_status =
      lastTransaction?.status ||
      gatewayData?.status ||
      pixPaymentData?.status ||
      '';

    return {
      // Informações do cliente
      ...this._buildSegmentUserEventProperties(userData),
      customer_document_number: userData.document_number || '',
      customer_name: userData.name || '',
      customer_email: userData.email || '',

      // Informações da transação
      transaction_id: transaction_id,
      transaction_status: transaction_status,

      // Informações do checkout
      plan_id: checkoutData.plan_id,
      plan_name: checkoutData.plan_name,
      plan_amount: checkoutData.plan_amount,
      plan_type: checkoutData.plan_type,
      coupon_code: checkoutData.coupon_code,
      coupon_valid: checkoutData.coupon_valid,
      trial_days: checkoutData.trial_days,
      has_compra_garantida: checkoutData.has_compra_garantida,

      // Metadados da operação
      timestamp: new Date().toISOString(),
      event_type:
        LoggedCheckoutTrackingService.EVENT_LOGGED_CHECKOUT_TALK_TO_SUPPORT_CLICKED,
      conversion_value: 0,
      action: 'talk_to_support_clicked',
      origin: origin,
    };
  }

  /**
   * Monta propriedades para `trackLoggedCheckoutPaymentMethodChanged` em trackEvents.ts.
   */
  prepareLoggedCheckoutPaymentMethodChangedForAnalytics(
    userData,
    checkoutData = {},
    fromMethod = '',
    toMethod = '',
    pixPaymentData = {},
  ) {
    const gatewayData = pixPaymentData?.gatewayData || {};
    const lastTransaction = gatewayData?.last_transaction || {};

    const transaction_id =
      lastTransaction?.id ||
      pixPaymentData?.transactionId ||
      pixPaymentData?.id ||
      '';

    const transaction_status =
      lastTransaction?.status ||
      gatewayData?.status ||
      pixPaymentData?.status ||
      '';

    return {
      ...this._buildSegmentUserEventProperties(userData),
      customer_document_number: userData.document_number || '',
      customer_name: userData.name || '',
      customer_email: userData.email || '',
      transaction_id,
      transaction_status,
      plan_id: checkoutData.plan_id,
      plan_name: checkoutData.plan_name,
      plan_amount: checkoutData.plan_amount,
      plan_type: checkoutData.plan_type,
      coupon_code: checkoutData.coupon_code,
      coupon_valid: checkoutData.coupon_valid,
      trial_days: checkoutData.trial_days,
      has_compra_garantida: checkoutData.has_compra_garantida,
      timestamp: new Date().toISOString(),
      event_type:
        LoggedCheckoutTrackingService.EVENT_LOGGED_CHECKOUT_PAYMENT_METHOD_CHANGED,
      conversion_value: 0,
      action: 'payment_method_changed',
      from_method: fromMethod,
      to_method: toMethod,
    };
  }

  _subscriptionReduxDataParser(subscriptionData) {
    const subscriptionParsed = {};
    const planParsed = {};

    planParsed.plan_id = subscriptionData?.plan_details?.id || '';
    planParsed.plan_name = subscriptionData?.plan_details?.name || '';
    planParsed.plan_amount = subscriptionData?.plan_details?.amount || 0;
    planParsed.plan_type = subscriptionData?.plan_details?.plan_type || '';
    planParsed.coupon_code = subscriptionData?.info?.coupon || '';
    planParsed.coupon_valid = subscriptionData?.info?.coupon ? true : false;
    // trial_days
    planParsed.trial_days = subscriptionData?.plan_details?.trial_days || 0;
    // has_compra_garantida
    planParsed.has_compra_garantida =
      subscriptionData?.has_compra_garantida || false;

    subscriptionParsed.subscription_id =
      subscriptionData?.info?.subscription_id || '';
    subscriptionParsed.subscription_status =
      subscriptionData?.info?.status || '';

    return {
      subscription: subscriptionParsed,
      plan: planParsed,
    };
  }

  /**
   * Monta propriedades para `trackLoggedCheckoutAccessBoletoUrl` em trackEvents.ts.
   */
  prepareLoggedCheckoutAccessBoletoUrlForAnalytics(userData, checkoutData) {
    const { subscription, plan } = this._subscriptionReduxDataParser(
      checkoutData,
    );
    const boletoDataUrl = checkoutData?.tickets?.[0]?.pdf || {};

    return {
      ...this._buildSegmentUserEventProperties(userData),
      customer_document_number: userData.document_number || '',
      customer_name: userData?.name || '',
      customer_email: userData?.email || '',
      plan_id: plan?.plan_id,
      plan_name: plan?.plan_name,
      plan_amount: plan?.plan_amount,
      plan_type: plan?.plan_type,
      coupon_code: plan?.coupon_code,
      coupon_valid: plan?.coupon_valid,
      trial_days: plan?.trial_days,
      has_compra_garantida: plan?.has_compra_garantida,
      subscription_id: subscription?.subscription_id,
      subscription_status: subscription?.subscription_status,
      boletoDataUrl,
      timestamp: new Date().toISOString(),
      event_type:
        LoggedCheckoutTrackingService.EVENT_LOGGED_CHECKOUT_PAYMENT_BOLETO_CLICKED,
      conversion_value: 0,
      action: 'access_boleto_url_clicked',
    };
  }

  /**
   * Monta propriedades para `trackLoggedCheckoutPayInvoiceButtonClicked` em trackEvents.ts.
   */
  prepareLoggedCheckoutPayInvoiceButtonClickedForAnalytics(
    userData,
    checkoutData,
    origin,
  ) {
    const { subscription, plan } = this._subscriptionReduxDataParser(
      checkoutData,
    );

    return {
      ...this._buildSegmentUserEventProperties(userData),
      plan_id: plan.plan_id,
      plan_name: plan.plan_name,
      plan_amount: plan.plan_amount,
      plan_type: plan.plan_type,
      coupon_code: plan.coupon_code,
      coupon_valid: plan.coupon_valid,
      trial_days: checkoutData.trial_days,
      has_compra_garantida: checkoutData.has_compra_garantida,
      subscription_id: subscription.subscription_id,
      subscription_status: subscription.subscription_status,
      timestamp: new Date().toISOString(),
      event_type:
        LoggedCheckoutTrackingService.EVENT_LOGGED_CHECKOUT_PAY_INVOICE_BUTTON_CLICKED,
      conversion_value: 0,
      action: 'pay_invoice_button_clicked',
      origin,
    };
  }
}

// Exportar instância singleton
// eslint-disable-next-line import/no-anonymous-default-export
export default new LoggedCheckoutTrackingService();
